/**
 * Story/pack cover — the approved illustration (`cover_image_url` /
 * `cover_image_thumb_url`, on stories and packs) when there is one, otherwise an
 * inline, sanitized SVG (`cover_svg` from the API) inside a small-radius
 * frame, or a striped "Papier de nuit" placeholder with the title when no
 * cover has been generated yet. An illustration that fails to load falls
 * back down the same chain (see `lib/coverSource.ts`).
 *
 * The frame carries a hairline in the line ink, and it is not decoration:
 * generated covers are painted on a full-bleed `#1d1733` ground
 * (`chouette_regen_covers.py`), which is exactly `--color-night`, the page's
 * own background. Without the rule a cover has no edge at all — it bleeds
 * into the page (Fabien, 2026-09-22). The `bg-surface` underneath never
 * showed for the same reason. Same token as a Card's border, so a cover
 * reads as a held object rather than as a second kind of surface.
 */
import { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { placeholderStyle } from "../lib/colorways";
import { coverImageSrc, coverKind, type CoverImageSize } from "../lib/coverSource";

export function Cover({
  svg,
  imageUrl,
  imageThumbUrl,
  imageSize = "thumb",
  title,
  colorway,
  colorKey,
  className = "",
  rounded = "rounded-[8px]",
  dimmed = false,
  showTitle = true,
}: {
  svg: string | null | undefined;
  /** `cover_image_url` — 1024px WebP, non-null only once approved. */
  imageUrl?: string | null;
  /** `cover_image_thumb_url` — 400px WebP, same rule. */
  imageThumbUrl?: string | null;
  /** Which of the two to load: "thumb" in grids and small tiles, "full"
   *  where the cover is shown large. */
  imageSize?: CoverImageSize;
  title: string;
  /** `cover_colorway` from the API — the family the real cover uses, so the
   *  placeholder doesn't change hue when that cover lands. */
  colorway?: string | null;
  /** Fallback key when the API sent no colorway — pass the story's slug (or
   *  its id) so a card keeps its colour across renders. Falls back to the
   *  title, which is stable too, except while a story is still being
   *  written and has none yet. */
  colorKey?: string;
  className?: string;
  rounded?: string;
  /** A failed story keeps its slot in the library but fades: the parent
   *  should see at a glance it is not one to tap for tonight. */
  dimmed?: boolean;
  /** The placeholder prints the title on the tile. Turn it off for tiny
   *  thumbnails that already sit next to the title (a list row): at 48px the
   *  label is just a cropped word. */
  showTitle?: boolean;
}) {
  const clean = useMemo(() => (svg ? DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } }) : null), [svg]);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imageSrc = coverImageSrc(imageUrl, imageThumbUrl, imageSize);
  const kind = coverKind(imageSrc, failedSrc, !!clean);
  const dim = dimmed ? " opacity-40 grayscale" : "";

  if (kind === "image" && imageSrc) {
    return (
      <div className={`overflow-hidden ${rounded} border border-line bg-surface aspect-square ${className}${dim}`}>
        <img
          src={imageSrc}
          alt={title}
          loading="lazy"
          decoding="async"
          className="block w-full h-full object-cover"
          onError={() => setFailedSrc(imageSrc)}
        />
      </div>
    );
  }

  if (kind === "svg" && clean) {
    return (
      <div
        className={`overflow-hidden ${rounded} border border-line bg-surface aspect-square ${className}${dim}`}
        role="img"
        aria-label={title}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }

  return (
    <div
      className={`overflow-hidden ${rounded} border border-line aspect-square cover-placeholder flex items-end ${showTitle ? "p-3" : ""} ${className}${dim}`}
      style={placeholderStyle(colorway, colorKey || title)}
      role="img"
      aria-label={title}
    >
      {/* `text-night` rather than `text-mint-ink`: the tile is no longer
          always mint, and an ink tied to one family would clash on the
          other three. */}
      {showTitle && (
        <span className="font-heading font-bold text-night text-sm leading-tight bg-paper/90 rounded px-2 py-1">
          {title}
        </span>
      )}
    </div>
  );
}
